import React, { useCallback, useMemo } from "react";
import type {
  NativeSyntheticEvent,
  TextInputKeyPressEventData,
} from "react-native";
import {
  ComposerProps,
  IMessage,
  InputToolbarProps,
  SendProps,
} from "react-native-gifted-chat";

import ChatInputToolbarView from "./ChatInputToolbarView";
import { sendOnEnter } from "../../../utils/enterToSend";

/**
 * The toolbar props GiftedChat actually passes to `renderInputToolbar`.
 * @remarks GiftedChat spreads its composer and send props into the toolbar
 * props at runtime (`GiftedChat/index.js` `inputToolbarProps`), but its
 * `InputToolbarProps` type does not declare them.
 */
type GiftedChatToolbarProps = InputToolbarProps<IMessage> &
  Pick<SendProps<IMessage>, "text" | "onSend"> & {
    textInputProps?: ComposerProps["textInputProps"] & {
      /** GiftedChat's own composer ref, added at runtime. */
      ref?: { current: { focus: () => void } | null };
    };
  };

/**
 * Focuses GiftedChat's composer through the ref it adds to `textInputProps`.
 * @param textInputProps - The composer props GiftedChat passes the toolbar
 * @remarks Read inside the Enter handler only, never during render.
 */
const focusComposer = (
  textInputProps: GiftedChatToolbarProps["textInputProps"]
): void => {
  textInputProps?.ref?.current?.focus();
};

/**
 * Props for ChatInputToolbarContainer
 */
interface ChatInputToolbarContainerProps {
  props: InputToolbarProps<IMessage>;
  windowWidth: number;
  isDark: boolean;
  isResearchToggleEnabled: boolean;
  isResearchMode: boolean;
  onToggleResearch: () => void;
  /** Whether a message may be sent now — the Send button's own condition. */
  canSend: boolean;
}

/**
 * Container component for chat input toolbar with research mode toggle
 * @param root0 - Component props
 * @param root0.props - Gifted Chat input toolbar props containing text input and send handlers
 * @param root0.windowWidth - Current window width for responsive layout
 * @param root0.isDark - Whether dark theme is active
 * @param root0.isResearchToggleEnabled - Whether research mode toggle button should be displayed
 * @param root0.isResearchMode - Whether deep research mode is currently enabled
 * @param root0.onToggleResearch - Callback invoked when research mode toggle is pressed
 * @param root0.canSend - Whether a message may be sent now (gates Enter-to-send)
 */
const ChatInputToolbarContainer = ({
  props,
  windowWidth,
  isDark,
  isResearchToggleEnabled,
  isResearchMode,
  onToggleResearch,
  canSend,
}: ChatInputToolbarContainerProps) => {
  const { text, onSend, textInputProps } = props as GiftedChatToolbarProps;

  // SE-8646: Enter sends exactly as GiftedChat's Send button does — trimmed
  // text, composer reset — and nothing while sending is not allowed, so a
  // draft typed during an answer survives the keypress. The send path
  // (ChatContainer.onSend) dismisses the keyboard, which blurs the composer
  // on web, so focus is handed back for a keyboard user to keep typing.
  const sendDraft = useCallback(() => {
    const draft = text?.trim();
    if (!canSend || !draft) return;
    onSend?.({ text: draft }, true);
    focusComposer(textInputProps);
  }, [canSend, text, onSend, textInputProps]);

  // Chains rather than replaces: a key handler already on the composer props
  // runs first.
  const handleKeyPress = useCallback(
    (event: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
      textInputProps?.onKeyPress?.(event);
      sendOnEnter(event, sendDraft);
    },
    [textInputProps, sendDraft]
  );

  const toolbarProps = useMemo(
    () =>
      ({
        ...props,
        textInputProps: {
          ...textInputProps,
          onKeyPress: handleKeyPress,
        },
      }) as InputToolbarProps<IMessage>,
    [props, textInputProps, handleKeyPress]
  );

  return (
    <ChatInputToolbarView
      props={toolbarProps}
      windowWidth={windowWidth}
      isDark={isDark}
      isResearchToggleEnabled={isResearchToggleEnabled}
      isResearchMode={isResearchMode}
      onToggleResearch={onToggleResearch}
    />
  );
};

export default ChatInputToolbarContainer;
